import { Card, Checkbox, Col, Divider, Form, Input, Row, Alert } from 'antd';
import dayjs from 'dayjs';
import UploadMultipleFile from '@/components/upload-multiple-file/UploadMultipleFile';

export default function OwnersInfoSection({
  owners,
  ownerStates,
  handleCheckboxChange,
  handleInputChange,
  qt,
  ownershipChangeFiles: _ownershipChangeFiles,
  setOwnershipChangeFiles,
  handleFileRemove,
  language,
  questionnaireDocs = [],
}) {
  if (!owners || owners.length === 0) {
    return (
      <Card
        title={qt('questionnaire.infoAboutOwners')}
        className="mb-5"
        style={{ marginBottom: '20px' }}
      >
        <p>{qt('questionnaire.noOwners')}</p>

        <Divider style={{ marginTop: '30px' }} />

        <h4 style={{ marginBottom: '15px', fontWeight: 'bold' }}>
          {qt('questionnaire.statementOnOwnershipStructure')}
        </h4>

        <Form.Item
          name="changesInActualOwnersSinceLastSubmission"
          valuePropName="checked"
          style={{ marginBottom: '10px' }}
        >
          <Checkbox>{qt('questionnaire.pleaseConfirm')}</Checkbox>
        </Form.Item>

        <Alert
          message={<span>❗❗❗ {qt('questionnaire.ifAnswerYes')}</span>}
          type="warning"
          showIcon
          style={{ marginTop: '15px', marginBottom: '15px' }}
        />

        <UploadMultipleFile
          onFilesChange={(files) => setOwnershipChangeFiles(files)}
          onFileRemove={handleFileRemove}
          maxFiles={10}
          language={language}
          initialFiles={questionnaireDocs}
        />
      </Card>
    );
  }

  return (
    <Card
      title={qt('questionnaire.infoAboutOwners')}
      className="mb-5"
      style={{ marginBottom: '20px' }}
    >
      {owners.map((owner, index) => {
        const ownerState = ownerStates[index] || {};

        return (
          <div key={index}>
            {index > 0 && <Divider />}

            <h4 style={{ marginBottom: '15px', fontWeight: 'bold' }}>
              {qt('questionnaire.beneficialOwner')} {index + 1}
            </h4>

            {/* Basic Owner Info */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={qt('questionnaire.firstLastName')}
                  style={{ marginBottom: '10px' }}
                >
                  <Input value={owner.name} disabled />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={qt('questionnaire.personalNumber')}
                  style={{ marginBottom: '10px' }}
                >
                  <Input
                    value={owner.personalNumber || owner.JMBG || ''}
                    disabled
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={qt('questionnaire.dateOfBirth')}
                  style={{ marginBottom: '10px' }}
                >
                  <Input
                    value={
                      owner.dateOfBirth
                        ? dayjs(owner.dateOfBirth).format('DD-MM-YYYY')
                        : ''
                    }
                    disabled
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={qt('questionnaire.placeOfBirth')}
                  style={{ marginBottom: '10px' }}
                >
                  <Input
                    value={
                      owner.placeOfBirth && owner.countryOfBirth
                        ? `${owner.placeOfBirth}, ${owner.countryOfBirth}`
                        : owner.placeOfBirth || owner.countryOfBirth || ''
                    }
                    disabled
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={qt('questionnaire.countryOfBirth')}
                  style={{ marginBottom: '10px' }}
                >
                  <Input value={owner.countryOfBirth} disabled />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={qt('questionnaire.streetNumberResidence')}
                  style={{ marginBottom: '10px' }}
                >
                  <Input value={owner.addressOfResidence} disabled />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={qt('questionnaire.placeCountryResidence')}
                  style={{ marginBottom: '10px' }}
                >
                  <Input
                    value={
                      owner.placeOfResidence && owner.countryOfResidence
                        ? `${owner.placeOfResidence}, ${owner.countryOfResidence}`
                        : owner.placeOfResidence ||
                          owner.countryOfResidence ||
                          ''
                    }
                    disabled
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={qt('questionnaire.percentageOfOwnership')}
                  style={{ marginBottom: '10px' }}
                >
                  <Input value={owner.percentageOfOwnership} disabled />
                </Form.Item>
              </Col>
            </Row>

            {/* Section 1: Basis of Beneficial Owner */}
            <Divider orientation="left">
              <strong>{qt('questionnaire.section1')}</strong>
            </Divider>

            <Row gutter={16}>
              <Col span={24}>
                <Checkbox
                  checked={ownerState.directOrIndirectOwns25 || false}
                  onChange={(e) =>
                    handleCheckboxChange(
                      index,
                      'directOrIndirectOwns25',
                      e.target.checked
                    )
                  }
                >
                  {qt('questionnaire.natural25')}
                </Checkbox>
              </Col>

              <Col span={24} style={{ marginTop: '10px' }}>
                <Checkbox
                  checked={ownerState.directOrIndirectPredominant || false}
                  onChange={(e) =>
                    handleCheckboxChange(
                      index,
                      'directOrIndirectPredominant',
                      e.target.checked
                    )
                  }
                >
                  {qt('questionnaire.naturalPermanent')}
                </Checkbox>
              </Col>

              <Col span={24} style={{ marginTop: '10px' }}>
                <Checkbox
                  checked={ownerState.indirectlyProvidesFunds || false}
                  onChange={(e) =>
                    handleCheckboxChange(
                      index,
                      'indirectlyProvidesFunds',
                      e.target.checked
                    )
                  }
                >
                  {qt('questionnaire.naturalProvides')}
                </Checkbox>
              </Col>

              <Col span={24} style={{ marginTop: '10px' }}>
                <Checkbox
                  checked={ownerState.entrepreneur || false}
                  onChange={(e) =>
                    handleCheckboxChange(
                      index,
                      'entrepreneur',
                      e.target.checked
                    )
                  }
                >
                  {qt('questionnaire.entrepreneur')}
                </Checkbox>
              </Col>

              <Col span={24} style={{ marginTop: '10px' }}>
                <Checkbox
                  checked={
                    ownerState.founderInManagementOfOtherForeignEntity || false
                  }
                  onChange={(e) =>
                    handleCheckboxChange(
                      index,
                      'founderInManagementOfOtherForeignEntity',
                      e.target.checked
                    )
                  }
                >
                  {qt('questionnaire.naturalFounder')}
                </Checkbox>
              </Col>

              <Col span={24} style={{ marginTop: '10px' }}>
                <Input.TextArea
                  rows={2}
                  placeholder={qt('questionnaire.naturalFounder')}
                  value={
                    ownerState.founderInManagementOfOtherForeignEntityList || ''
                  }
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      'founderInManagementOfOtherForeignEntityList',
                      e.target.value
                    )
                  }
                />
              </Col>
            </Row>

            {/* Section 2: Direct Ownership */}
            <Divider style={{ marginTop: '20px' }} />

            <Row gutter={16}>
              <Col span={24}>
                <Checkbox
                  checked={ownerState.individualDirectOwnerOfCompany || false}
                  onChange={(e) =>
                    handleCheckboxChange(
                      index,
                      'individualDirectOwnerOfCompany',
                      e.target.checked
                    )
                  }
                >
                  {qt('questionnaire.isIndividualDirectOwnerClient')}
                </Checkbox>
              </Col>

              <Col span={24} style={{ marginTop: '10px' }}>
                <Form.Item
                  label={qt('questionnaire.nameAndRegisteredOffice')}
                  style={{ marginBottom: '10px' }}
                >
                  <Input.TextArea
                    rows={2}
                    value={ownerState.nameOfLegalEntityOwnersOfCompany || ''}
                    onChange={(e) =>
                      handleInputChange(
                        index,
                        'nameOfLegalEntityOwnersOfCompany',
                        e.target.value
                      )
                    }
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <p style={{ fontStyle: 'italic', color: '#666' }}>
                  {qt('questionnaire.alongWith')}
                </p>
              </Col>
            </Row>

            {/* Section 3: Public Official Status */}
            <Divider orientation="left">
              <strong>{qt('questionnaire.section3')}</strong>
            </Divider>

            <Row gutter={16}>
              <Col span={24}>
                <Checkbox
                  checked={ownerState.officialOfDomesticState || false}
                  onChange={(e) =>
                    handleCheckboxChange(
                      index,
                      'officialOfDomesticState',
                      e.target.checked
                    )
                  }
                >
                  {qt('questionnaire.stateOfficial')}
                </Checkbox>
              </Col>

              <Col span={24} style={{ marginTop: '10px' }}>
                <Checkbox
                  checked={ownerState.officialOfForeignState || false}
                  onChange={(e) =>
                    handleCheckboxChange(
                      index,
                      'officialOfForeignState',
                      e.target.checked
                    )
                  }
                >
                  {qt('questionnaire.foreignOfficial')}
                </Checkbox>
              </Col>

              <Col span={24} style={{ marginTop: '10px' }}>
                <Checkbox
                  checked={
                    ownerState.officialOfInternationalOrganization || false
                  }
                  onChange={(e) =>
                    handleCheckboxChange(
                      index,
                      'officialOfInternationalOrganization',
                      e.target.checked
                    )
                  }
                >
                  {qt('questionnaire.internationalOfficial')}
                </Checkbox>
              </Col>
            </Row>
          </div>
        );
      })}

      {/* Statement on Ownership Changes */}
      <Divider style={{ marginTop: '30px' }} />

      <h4 style={{ marginBottom: '15px', fontWeight: 'bold' }}>
        {qt('questionnaire.statementOnOwnershipStructure')}
      </h4>

      <Form.Item
        name="changesInActualOwnersSinceLastSubmission"
        valuePropName="checked"
        style={{ marginBottom: '10px' }}
      >
        <Checkbox>{qt('questionnaire.pleaseConfirm')}</Checkbox>
      </Form.Item>

      <Alert
        message={<span>❗❗❗ {qt('questionnaire.ifAnswerYes')}</span>}
        type="warning"
        showIcon
        style={{ marginTop: '15px', marginBottom: '15px' }}
      />

      <UploadMultipleFile
        onFilesChange={(files) => setOwnershipChangeFiles(files)}
        onFileRemove={handleFileRemove}
        maxFiles={10}
        language={language}
        initialFiles={questionnaireDocs}
      />
    </Card>
  );
}
